import type { FilterRuleModel } from '@/shared/components/filter/types'
import type { SRT_TableInstance } from '@/shared/components/master-table'
import type { AnchorResponseModel, FieldSchemaModel } from '@/shared/generated'

import { useCallback, useMemo } from 'react'
import { map } from 'lodash/fp'
import { PlusIcon, Trash2Icon, UploadIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	formatFilterRule,
	isFilterbaleField,
	toAdaptedSchema,
} from '@/entities/column'
import { useStoredFiltersKeys } from '@/entities/user'

import { QuickFiltersBar } from '@/widgets/quick-filters-bar'
import { SavedReportIndicator } from '@/widgets/saved-report-indicator'
import { SavedReportPicker } from '@/widgets/saved-report-picker'

import TableFilters from '@/shared/components/data-grid/TableFilters'
import TextSearchField from '@/shared/components/data-grid/TextSearchField'
import { ColumnsDrawer } from '@/shared/components/datasheet/components/ColumnsDrawer'
import { Operators } from '@/shared/components/query-builder'
import { SelectFilter } from '@/shared/components/refinement-bar'
import { getFiledConfig } from '@/shared/components/refinement-bar/helpers'
import { Button } from '@/shared/components/ui/button'
import { Header } from '@/shared/components/ui/layout'
import { Separator } from '@/shared/components/ui/separator'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import {
	FieldType,
	SavedFilterType,
	ValueEditorType,
	ValueType,
} from '@/shared/generated'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { ampli } from '@/shared/lib/ampli'
import { useStore } from '@/shared/lib/datasheet/store'

import { useColumnChoices } from '../../lib/useColumnChoices'
import { useAnchorsManagementContext } from '../AnchorManagementProvider'
import AnchorAssortmentTableAlertsButton from './AnchorAssortmentTableAlertsButton'

export function AnchorAssortmentTableToolbar({
	table,
}: {
	table: SRT_TableInstance<AnchorResponseModel>
}) {
	const { t } = useTranslation()
	const rowSelection = useStore((state) => state.rowSelection)
	const schema = useStore((state) => state.schema)

	const [storedQuickFiltersKeys, saveStoredQuickFiltersKeys] =
		useStoredFiltersKeys('acnchors_quick_filters_keys')
	const [{ search: searchText, filters }, setQuery] = useLocationQuery()
	const {
		handleDeleteSelectedRows,
		handleImportReportClick,
		handleCreateAnchorSku,
	} = useAnchorsManagementContext()
	const dataChoices = useColumnChoices()
	const selectedCount = useMemo(
		() => Object.keys(rowSelection).length,
		[rowSelection]
	)
	const filterSchema = [
		...schema.filter(isFilterbaleField),
		{
			id: 'pos_ids',
			field_type: FieldType.Tree,
			reference: {
				entity: 'sales_entity',
				accessor: 'pos_ids',
				field: 'name',
			},
			params: {
				editable: false,
				searchable: true,
				filterable: true,
				sortable: false,
			},
			value_type: ValueType.Array,
			value_editor_type: ValueEditorType.CheckboxTree,
			name: 'field_schema_pos_id',
		},
	] as FieldSchemaModel[]
	const setFilters = (filters: FilterRuleModel[]) =>
		setQuery({ filters: filters?.length ? filters : null })
	const getAlertsSelectOptions = useCallback(
		() => ({
			data: dataChoices.alerts,
		}),
		[dataChoices.alerts]
	)

	return (
		<Header className='shrink-0 items-center justify-between py-2'>
			<div className='flex items-center space-x-1'>
				<TextSearchField
					value={searchText}
					onChange={(search) => setQuery({ search })}
				/>
				<SavedReportPicker
					type={SavedFilterType.Anchors}
					onItemClick={(filterItem) => {
						ampli.savedFiltersClickOnFilter({
							from: 'anchor assortment',
						})
						setFilters(filterItem.filters!)
					}}
				/>
				<TableFilters
					enableSaveFilters
					savedFiltersType={SavedFilterType.Anchors}
					onAnalyticsApply={() => ampli.anchoringApplyFilters()}
					choices={dataChoices!}
					schema={filterSchema as FieldSchemaModel[]}
					filters={filters ?? []}
					onApply={setFilters}
				/>
				<Separator orientation='vertical' className='h-5!' />
				<QuickFiltersBar
					rules={filters ?? []}
					fieldsConfig={{
						...getFiledConfig(
							filterSchema.map((schema) => toAdaptedSchema(schema, t)),
							dataChoices
						),
						alerts: {
							label: t('field_schema_alerts', { ns: 'fields' }),
							type: SelectFilter,
							getInitialValue: () => ({
								operation: Operators.ANY_OF,
								value: [],
							}),
							useSelectOptions: getAlertsSelectOptions,
						},
					}}
					storedKeys={storedQuickFiltersKeys}
					onSaveStoredKeys={saveStoredQuickFiltersKeys}
					onApply={(rules) => {
						setFilters(rules.map(formatFilterRule))
					}}
				/>
				{selectedCount > 0 && (
					<Button
						size='sm'
						variant='secondary'
						onClick={handleDeleteSelectedRows}
					>
						<Trash2Icon />
						{t('pc.action.bulk_delete', { value: selectedCount })}
					</Button>
				)}
			</div>
			<div className='flex items-center space-x-1'>
				<SavedReportIndicator
					type={SavedFilterType.Anchors}
					filters={filters}
					disabled={!filters?.length}
				/>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							key='create_anchor_sku'
							size='icon'
							variant='primary'
							onClick={handleCreateAnchorSku}
						>
							<PlusIcon />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						{t('anchoring_management.create_anchor_sku.tooltip')}
					</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button key='import' onClick={handleImportReportClick} size='icon'>
							<UploadIcon />
						</Button>
					</TooltipTrigger>
					<TooltipContent>{t('app.update_data')}</TooltipContent>
				</Tooltip>
				<Separator orientation='vertical' className='h-5!' />
				<AnchorAssortmentTableAlertsButton />
				<ColumnsDrawer table={table} />
			</div>
		</Header>
	)
}
